import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BeneficiaryDashboard.css';
import BeneficiaryCard from './BeneficiaryCard';
import AddBeneficiaryModal from './AddBeneficiaryModal';
import PaymentModal from './PaymentModal';
import QRShareModal from './QRShareModal';
import SuggestionsPanel from './SuggestionsPanel';
import QRScanner from './QRScanner';

interface Beneficiary {
  id: string;
  name: string;
  email: string;
  accountNumber: string;
  trustLevel: 'public' | 'semi-private' | 'anonymous';
  lastPayment: string;
  paymentCount: number;
  isVerified: boolean;
  discoveryMethod: string;
  trustScore?: number;
  riskLevel?: 'low' | 'medium' | 'high';
}

interface BeneficiaryDashboardProps {
  socket: any;
  onAlert?: (message: string, type: string) => void;
}

const BeneficiaryDashboard: React.FC<BeneficiaryDashboardProps> = ({ socket, onAlert }) => {
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQRShareModal, setShowQRShareModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTrust, setFilterTrust] = useState<string>('all');

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      const response = await fetch('/api/beneficiaries');
      const data = await response.json();
      setBeneficiaries(data);
    } catch (error) {
      console.error('Error fetching beneficiaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBeneficiary = async (beneficiaryData: any) => {
    try {
      const response = await fetch('/api/beneficiaries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(beneficiaryData),
      });
      
      if (response.ok) {
        const newBeneficiary = await response.json();
        setBeneficiaries(prev => [newBeneficiary, ...prev]);
        setShowAddModal(false);
      }
    } catch (error) {
      console.error('Error adding beneficiary:', error);
    }
  };

  const handleQRScan = async (data: string) => {
    try {
      const response = await fetch('/api/scan-qr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ qrData: data }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await handleAddBeneficiary(result.beneficiary);
          setShowQRScanner(false);
        }
      }
    } catch (error) {
      console.error('Error scanning QR code:', error);
    }
  };

  const handlePayment = (beneficiary: Beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setShowPaymentModal(true);
  };

  const handlePaymentInitiated = (paymentData: any) => {
    onAlert?.(`Payment of R${paymentData.amount} initiated to ${selectedBeneficiary?.name}`, 'info');
    setShowPaymentModal(false);
    setSelectedBeneficiary(null);
  };

  const handleQRShare = (beneficiary: Beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setShowQRShareModal(true);
  };

  const filteredBeneficiaries = beneficiaries.filter(beneficiary => {
    const matchesSearch = beneficiary.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         beneficiary.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTrust = filterTrust === 'all' || beneficiary.trustLevel === filterTrust;
    return matchesSearch && matchesTrust;
  });

  useEffect(() => {
    socket.on('new-alert', (alert: any) => {
      // existing alert handler
    });

    socket.on('beneficiary-added', (beneficiary: any) => {
      // existing handler
    });

    socket.on('verification-complete', (verification: any) => {
      // existing handler
    });

    // New: handle payment completion
    socket.on('payment-completed', ({ payment, beneficiary: name }: { payment: any; beneficiary: any }) => {
      onAlert?.(`Payment of R${payment.amount} to ${name} completed`, 'info');
      setBeneficiaries(prev => prev.map(b =>
        b.id === payment.beneficiaryId
          ? { ...b, paymentCount: b.paymentCount + 1, lastPayment: payment.timestamp }
          : b
      ));
    });

    // New: handle update request sent
    socket.on('update-request-sent', ({ beneficiary: name }: { beneficiary: any }) => {
      onAlert?.(`Update request sent for ${name}`, 'info');
    });

    return () => {
      socket.off('new-alert');
      socket.off('beneficiary-added');
      socket.off('verification-complete');
      socket.off('payment-completed');
      socket.off('update-request-sent');
    };
  }, [socket, onAlert]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <motion.div 
          className="loading-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <p>Loading beneficiaries...</p>
      </div>
    );
  }

  return (
    <div className="beneficiary-dashboard">
      <motion.div 
        className="dashboard-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="header-left">
          <h1>Beneficiary Dashboard</h1>
          <p>Discover, manage, and pay recipients with confidence</p>
        </div>
        
        <div className="header-actions">
          <motion.button
            className="action-btn primary"
            onClick={() => setShowAddModal(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="btn-icon">+</span>
            Add Beneficiary
          </motion.button>
          
          <motion.button
            className="action-btn secondary"
            onClick={() => setShowQRScanner(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="btn-icon">📱</span>
            Scan QR
          </motion.button>
        </div>
      </motion.div>

      <div className="dashboard-content">
        <div className="left-panel">
          <SuggestionsPanel onAddBeneficiary={handleAddBeneficiary} />
          
          <motion.div 
            className="search-filters"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search beneficiaries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <div className="search-icon">🔍</div>
            </div>
            
            <select
              value={filterTrust}
              onChange={(e) => setFilterTrust(e.target.value)}
              className="trust-filter"
            >
              <option value="all">All Trust Levels</option>
              <option value="public">Public</option>
              <option value="semi-private">Semi-Private</option>
              <option value="anonymous">Anonymous</option>
            </select>
          </motion.div>
        </div>

        <div className="beneficiaries-grid">
          <AnimatePresence>
            {filteredBeneficiaries.map((beneficiary, index) => (
              <motion.div
                key={beneficiary.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
              >
                <BeneficiaryCard 
                  beneficiary={beneficiary} 
                  socket={socket}
                  onAlert={onAlert}
                  onPayment={handlePayment}
                  onShare={handleQRShare}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          
          {filteredBeneficiaries.length === 0 && (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="empty-icon">👥</div>
              <h3>No beneficiaries found</h3>
              <p>Try adjusting your search or add a new beneficiary</p>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showAddModal && (
          <AddBeneficiaryModal
            onClose={() => setShowAddModal(false)}
            onAdd={handleAddBeneficiary}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQRScanner && (
          <QRScanner
            onClose={() => setShowQRScanner(false)}
            onScan={handleQRScan}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPaymentModal && selectedBeneficiary && (
          <PaymentModal
            beneficiary={selectedBeneficiary}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedBeneficiary(null);
            }}
            onPaymentInitiated={handlePaymentInitiated}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQRShareModal && selectedBeneficiary && (
          <QRShareModal
            beneficiary={selectedBeneficiary}
            onClose={() => {
              setShowQRShareModal(false);
              setSelectedBeneficiary(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BeneficiaryDashboard;
